import { useEffect, useState } from 'react';
import useAuthentication from '~/hooks/use-authentication';
import api, { useConfig } from '~/lib/api';
import regionsByCountry from './regions-by-country.json';

function fromApi(networks) {
  networks = networks.map(n => ({
    mcc: n.mcc,
    mnc: n.mnc,
    country: n.country,
    countryCode: n.country_code,
    name: n.name,
    accessCharge: n.access_charge,
    kbIncrement: n.kb_increment,
    pricePerMb: n.price_per_mb,
    smsFromDeviceCost: n.sms_from_device_cost,
    permanentRoamingRestriction: n.permanent_roaming_restriction,
    '2G': n['2G'],
    '3G': n['3G'],
    '4G': n['4G'],
    nbIot: n.nb_iot,
    lteM: n.lte_m,
    ipxProvider: n.ipx_provider,
  }));

  const countries = networks
    .sort((a, b) => {
      const countryDiff = a.country.localeCompare(b.country);
      if (countryDiff !== 0) return countryDiff;
      return a.name.localeCompare(b.name);
    })
    .reduce((countries, network) => {
      const existingCountry = countries.find(c => c.countryCode === network.countryCode);
      const hasCountry = !!existingCountry;
      const country = hasCountry
        ? existingCountry
        : {
            countryCode: network.countryCode,
            country: network.country,
            mccs: [],
          };
      country.mccs = [...new Set([...country.mccs, network.mcc])];

      if (!hasCountry) countries.push(country);

      return countries;
    }, []);

  function upsertRegion({ regions, name, mcc }) {
    const existingRegion = regions.find(r => r.name === name);
    const hasRegion = !!existingRegion;
    if (hasRegion) {
      existingRegion.mccs = [...new Set([...existingRegion.mccs, mcc])];
      return;
    }

    const region = hasRegion
      ? existingRegion
      : {
          name,
          mccs: [],
        };
    region.mccs = [...new Set([...region.mccs, mcc])];
    regions.push(region);
  }

  const regions = networks
    .reduce((regions, network) => {
      const countryRegionInformation = regionsByCountry.find(r => r.countryCode === network.countryCode);
      if (countryRegionInformation?.region)
        upsertRegion({ regions, name: countryRegionInformation.region, mcc: network.mcc });
      return regions;
    }, [])
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    networks,
    countries,
    regions,
  };
}

function useAllNetworks() {
  const { isGod, isDemiGod, assumedOrganizationId } = useAuthentication();
  const getConfig = useConfig(assumedOrganizationId);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const isGodOrDemiGod = isGod || isDemiGod;
    const shouldApplyGetConfig = isGodOrDemiGod && assumedOrganizationId;

    async function get() {
      try {
        const response = await api.get('/networks', shouldApplyGetConfig ? getConfig() : undefined);
        setData(fromApi(response.data));
        setLoading(false);
      } catch (e) {
        setError(e);
        setLoading(false);
      }
    }

    setLoading(true);
    get();
  }, [isGod, isDemiGod, assumedOrganizationId, getConfig]);

  return {
    loading,
    error,
    data,
  };
}

export default useAllNetworks;
